import { ESortOrder } from "@/modules/users/users.const";
import { Header, defaultUser, downloadCsv, initPage } from "@/modules/users/users.const";
import { RoleType, Roles } from "@/store/auth/auth.type";
import { useCallback, useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { CreateNewUserModal } from "@/modules/users/components/create-new-user-modal";
import { Loader2 } from "lucide-react";
import { SERVICE_ACCOUNTS_TAB } from "@/modules/users/service-accounts.const";
import { SortingState } from "@tanstack/react-table";
import { Toaster } from "@/components/ui/sonner";
import { TabType } from "@/modules/users/users.type";
import { UserPanel } from "@/modules/users/components/users-panel";
import { Wrapper } from "@/components/layout";
import { stringifyQueryString } from "@/helper/utils";
import { useCities } from "@/modules/cities/components/hooks/use-cities.hook";
import { useDebounceWithCancel } from "@/hooks/lib-ui";
import { useDownloadUsersCvsByRole } from "@/modules/users/hooks/use-users";
import { useGetClients } from "@/modules/users/hooks/use-clients";
import useToggle from "@/hooks/use-toggle";
import { useUsersWithCities } from "@/modules/users/hooks/use-users-with-cities";

export const UsersPage = () => {
  const { state } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = searchParams.get("page");

  const rawRoleParam = searchParams.get("role");
  const isValidRole = rawRoleParam != null && Header.includes(rawRoleParam as TabType);
  const roleParam: TabType = isValidRole ? (rawRoleParam as TabType) : (state?.role ?? Roles.ALL);

  if (!isValidRole) {
    window.history.replaceState(
      null,
      "",
      `${location.pathname}?${stringifyQueryString({ role: roleParam, page: state?.page ?? initPage.pageIndex })}`,
    );
  }

  const [userType, setUserType] = useState<TabType>(roleParam);
  const pageSizeParam = searchParams.get("pageSize");
  const parsedPageSize = pageSizeParam ? parseInt(pageSizeParam, 10) : NaN;
  const pageSize = !isNaN(parsedPageSize) ? parsedPageSize : (initPage.pageSize ?? 10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, cancelDebounce] = useDebounceWithCancel(search, 400);
  const [cityId, setCityId] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const isInitialRender = useRef(true);
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  useEffect(() => {
    setSearch("");
    cancelDebounce("");
    setCityId("");
    setSorting((prev) => (prev.length === 0 ? prev : []));
  }, [userType, cancelDebounce]);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    setSearchParamsRef.current((params) => {
      params.set("page", "1");
      return params;
    });
  }, [debouncedSearch, cityId, sorting]);

  const pageIndex = pageParam ? parseInt(pageParam, 10) || initPage.pageIndex : initPage.pageIndex;

  const currentPage = useMemo(
    () => ({
      ...initPage,
      pageIndex: pageIndex,
      pageSize: pageSize,
      ...(debouncedSearch ? { searchString: debouncedSearch } : {}),
      ...(cityId ? { cities: cityId } : {}),
    }),
    [pageIndex, pageSize, debouncedSearch, cityId],
  );

  const apiFilter = useMemo(() => {
    if (sorting.length === 0) return currentPage;
    return {
      ...currentPage,
      sortField: sorting[0].id,
      sortOrder: sorting[0].desc ? ESortOrder.desc : ESortOrder.asc,
    };
  }, [currentPage, sorting]);

  const onPageSizeChange = (size: number) => {
    setSearchParams({ role: userType, page: "1", pageSize: String(size) });
  };

  const isServiceAccounts = userType === SERVICE_ACCOUNTS_TAB;
  const isClient = userType === Roles.CLIENT;
  const isUserRole = !isServiceAccounts && !isClient;
  const roleType = isServiceAccounts ? undefined : (userType as RoleType);

  const {
    mappedUsers,
    loading: usersLoading,
    refetch,
  } = useUsersWithCities(roleType ?? Roles.SYS_ADMIN, apiFilter, isUserRole);

  const { clients, loading: clientsLoading } = useGetClients(apiFilter, isClient);

  const users = isClient ? clients : mappedUsers;
  const loading = isServiceAccounts ? false : isClient ? clientsLoading : usersLoading;

  const { loading: downloadCSVLoading, fetchUsersCSV } = useDownloadUsersCvsByRole(roleType);
  const { cities, loading: citiesLoading } = useCities();
  const [showUserModal, setShowUserModal] = useToggle(false);

  const onChangePage = (page: number) => {
    setSearchParams({ role: userType, page: page.toString(), ...(pageSizeParam ? { pageSize: pageSizeParam } : {}) });
  };

  const handleExportCsv = useCallback(async () => {
    try {
      const csvData = await fetchUsersCSV();
      if (!csvData?.length || citiesLoading || !cities) return;
      if (!roleType) return;
      downloadCsv(csvData, cities, roleType);
    } catch (err) {
      return err;
    }
  }, [fetchUsersCSV, cities, citiesLoading, roleType]);

  const showUserActions = !loading && !isServiceAccounts && userType !== Roles.CLIENT;

  return (
    <Wrapper title="Users" showCityList={false} className="overflow-y-scroll">
      <div className="flex p-5 pb-2 w-[97%] mx-auto bg-white shadow-xs rounded-lg relative">
        <div className="w-full">
          <UserPanel
            loading={loading}
            users={users?.results ?? []}
            userType={userType}
            currentPage={currentPage}
            totalPage={users?.totalPages ?? 0}
            setCurrentPage={onChangePage}
            setUserType={setUserType}
            onRefetch={refetch}
            searchValue={search}
            onSearchChange={setSearch}
            {...(isClient ? {} : { cities, cityId, onCityChange: setCityId })}
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            sorting={sorting}
            onSortingChange={setSorting}
          />
        </div>
        <div className="w-auto flex justify-end absolute right-4 top-6 ">
          {showUserActions && (
            <>
              <Button
                onClick={setShowUserModal}
                variant="outline"
                className="text-xs font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md h-9"
              >
                <span className="hidden lg:inline">create</span>
              </Button>
              {userType === Roles.VIEWER &&
                (downloadCSVLoading ? (
                  <Button
                    variant="outline"
                    className="ml-2 text-xs font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md h-9"
                  >
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    downloading...
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    className="ml-2 text-xs font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md h-9"
                    onClick={() => handleExportCsv()}
                  >
                    <span className="hidden lg:inline">Export CSV</span>
                  </Button>
                ))}
            </>
          )}
        </div>
      </div>
      {showUserModal && !isServiceAccounts && (
        <CreateNewUserModal
          user={{
            ...defaultUser,
            // Pre-select the current tab's role only when the tab maps to a real
            // Auth0 role. The "All" tab is a UI grouping, not a role the backend
            // would accept.
            ...(roleType && roleType !== Roles.ALL ? { roles: [roleType] } : {}),
          }}
          onClose={setShowUserModal}
          onSuccess={refetch}
        />
      )}
      <Toaster position="bottom-right" />
    </Wrapper>
  );
};
